const API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const GROQ_CHAT_URL = 'https://api.groq.com/openai/v1/chat/completions';

/**
 * Groq free-tier models to try in order (OpenAI-compatible chat API).
 * See https://console.groq.com/docs/models
 */
const MODEL_NAMES = [
  'llama-3.3-70b-versatile',
  'llama-3.1-8b-instant',
];

class AIService {
  constructor() {
    this.workingModelName = null;
  }

  ensureApiKey() {
    if (!API_KEY || String(API_KEY).trim() === '') {
      throw new Error(
        'Missing REACT_APP_GROQ_API_KEY. Create a free API key at https://console.groq.com/ ' +
        'and add it to your .env file.'
      );
    }
  }

  modelOrder() {
    if (!this.workingModelName) return MODEL_NAMES;
    const rest = MODEL_NAMES.filter((m) => m !== this.workingModelName);
    return [this.workingModelName, ...rest];
  }

  async groqChatCompletion(prompt) {
    this.ensureApiKey();
    let lastError;

    for (const model of this.modelOrder()) {
      try {
        const response = await fetch(GROQ_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${API_KEY}`,
          },
          body: JSON.stringify({
            model,
            messages: [{ role: 'user', content: prompt }],
            temperature: 0.7,
            max_tokens: 8192,
          }),
        });

        const raw = await response.text();
        if (!response.ok) {
          throw new Error(`Groq HTTP ${response.status}: ${raw}`);
        }

        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error('Invalid JSON from Groq API');
        }

        const text = data.choices?.[0]?.message?.content;
        if (!text || typeof text !== 'string') {
          throw new Error('Empty or invalid Groq completion');
        }

        this.workingModelName = model;
        console.log(`Using Groq model: ${model}`);
        return text.trim();
      } catch (error) {
        console.warn(`Groq model ${model} failed:`, error.message);
        lastError = error;
      }
    }

    throw (
      lastError ||
      new Error(
        `Unable to complete request with any Groq model. Tried: ${MODEL_NAMES.join(', ')}`
      )
    );
  }

  /**
   * Generate motorcycle recommendations based on user's inputs
   * @param {Object} userInputs - User inputs from the form
   * @returns {Promise<Array>} - Array of recommended motorcycles
   */
  async generateRecommendations(userInputs) {
    try {
      let prompt = `You are an expert motorcycle advisor. Please recommend 3 motorcycles based on the following criteria:
      
      - Riding experience: ${userInputs.experience} years
      ${userInputs.experience > 0 ? `- Previous bike owned: ${userInputs.previousBike}` : ''}
      - Budget: ${userInputs.budget} lakhs
      ${userInputs.preferences ? `- Preferences: ${userInputs.preferences}` : ''}
      
      Important guidelines:
      1. If the rider has less than 1 year of experience, do not recommend bikes above 49 BHP.
      2. If the rider previously owned a bike, recommend at least two options from a similar category (cruiser, street, sports, or adventure).
      3. All recommendations must be within the specified budget.
      4. For each motorcycle recommendation, include a list of pros (advantages) and cons (disadvantages).
      5. For each bike, include a personalized explanation of why this specific bike is recommended based on the rider's experience level, previous bike (if any), and preferences. This should be insightful and tailored to the specific user profile.
      6. DO NOT recommend BMW G 310 GS or G 310 R as they are discontinued models.
      7. Format each recommendation with the following details:
         - Bike name and model
         - Price in lakhs
         - Engine displacement in cc
         - Power output in BHP
         - Key features (2-3 points)
         - Personalized explanation (2-3 sentences on why this bike is suitable for this specific rider)
         - Pros (3 advantages of the bike)
         - Cons (3 disadvantages or limitations of the bike)
         - URL in format: https://www.bikewale.com/[brand]-bikes/[model]/
      8. Return your response as valid JSON with this structure: 
      [
        {
          "name": "Bike name",
          "price": "Price in lakhs",
          "displacement": "Engine cc",
          "power": "BHP",
          "features": ["Feature 1", "Feature 2", "Feature 3"],
          "explanation": "Personalized explanation of why this bike is recommended for this rider",
          "pros": ["Pro 1", "Pro 2", "Pro 3"],
          "cons": ["Con 1", "Con 2", "Con 3"],
          "url": "URL to bikewale.com"
        },
        ...
      ]`;

      const text = await this.groqChatCompletion(prompt);

      const jsonStartIndex = text.indexOf('[');
      const jsonEndIndex = text.lastIndexOf(']') + 1;

      if (jsonStartIndex === -1 || jsonEndIndex === -1) {
        throw new Error('Invalid response format from AI');
      }

      const jsonStr = text.substring(jsonStartIndex, jsonEndIndex);
      const bikes = JSON.parse(jsonStr);

      return this.fixBikeWaleUrls(bikes);
    } catch (error) {
      console.error('Error in AI service:', error);
      throw error;
    }
  }

  /**
   * Fix URLs for specific bike models to ensure correct Bikewale links
   * @param {Array} bikes - Array of bike recommendations
   * @returns {Array} - Array with corrected URLs
   */
  fixBikeWaleUrls(bikes) {
    return bikes.map((bike) => {
      if (bike.url) {
        if (bike.url.includes('royal-enfield-bikes')) {
          bike.url = bike.url.replace('royal-enfield-bikes', 'royalenfield-bikes');
        }

        if (bike.url.includes('fz-s-fi') || bike.url.includes('fz-s-fi-v3')) {
          bike.url = bike.url.replace(/fz-s-fi(-v3)?/g, 'fzs-fi-v4');
        }
      }
      return bike;
    });
  }

  /**
   * Generate a personalized recommendation summary based on user's inputs
   * @param {Object} userInputs - User inputs from the form
   * @returns {Promise<String>} - Personalized recommendation summary
   */
  async generateRecommendationSummary(userInputs) {
    try {
      let prompt = `As a motorcycle expert advisor, write a personalized 2-3 sentence summary explaining how you've analyzed the user's information to provide motorcycle recommendations. Make it conversational as if you're speaking directly to the user. The summary should be tailored to these specific criteria:
      
      - Riding experience: ${userInputs.experience} years
      ${userInputs.experience > 0 ? `- Previous bike owned: ${userInputs.previousBike}` : ''}
      - Budget: ${userInputs.budget} lakhs
      ${userInputs.preferences ? `- Preferences: ${userInputs.preferences}` : ''}
      
      The summary should mention specific details from the user's inputs and explain how you've used this information to find their ideal motorcycles. Make it sound helpful and personalized without being too technical.
      
      Return ONLY the summary text with no additional formatting or explanation.`;

      const text = await this.groqChatCompletion(prompt);
      return text;
    } catch (error) {
      console.error('Error generating recommendation summary:', error);
      return "Based on your riding experience, preferences, and budget, I've carefully analyzed thousands of motorcycles to find these perfect matches for you. Each recommendation considers your specific riding style, technical requirements, and value for money to ensure you find a motorcycle that truly fits your needs.";
    }
  }
}

const aiService = new AIService();
export default aiService;
